import { AxiosResponse } from 'axios'
import { SagaIterator } from 'redux-saga'
import { call, put, select, SelectEffect } from 'redux-saga/effects'

import { isFetchResultSuccessful } from '../../@paco/entities/FetchResult'
import {
    NotificationCenterAction,
    NotificationCenterActionCategory,
    NotificationCenterCategory,
    NotificationCenterLog,
} from '../../@paco/entities/NotificationCenter/NotificationCenter'
import { endEventLoggerApiCall, startEventLoggerApiCall } from '../../@paco/entities/NotificationCenter/NotificationCenterService'

import { postCommentApiCall } from '../../helpers/api/commentsApi'
import { getShiftById, registerShift, unregisterShift } from '../../helpers/api-ts/shiftsApi'
import { getObjProperty } from '../../helpers/tools'
import { translate } from '../../helpers/translations/translator'
import { ShiftPlanningResource, ShiftResource } from '../../models'
import { SET_LOADING, SHIFT_FAILURE, SHIFT_SUCCESS } from '../actionTypes'

/**
 * Selects current user ID from the Redux state.
 */
function* selectCurrentUserId(): Generator<SelectEffect, string, any> {
    const state = yield select()
    return getObjProperty(state, 'userReducer.currentUser.id')
}

/**
 * Posts a comment to the user's shift planning entity.
 */
function* postCommentToShiftPlanning(shiftId: string, comment: string, eventLogTrackerData: NotificationCenterLog) {
    const userId: string = yield selectCurrentUserId()

    const response: AxiosResponse<ShiftResource> = yield call(() => getShiftById(['shiftPlannings', 'shiftPlannings.user'], shiftId))

    const userShiftPlanning = response.data.shiftPlannings.find((shiftPlanning: ShiftPlanningResource) => shiftPlanning.user?.id === userId)

    if (! userShiftPlanning) {
        throw new Error('User shift planning not found')
    }

    yield call(postCommentApiCall, {
        comment,
        relationshipType: 'shiftPlannings',
        relationshipKey: 'shiftPlanning',
        relationshipId: userShiftPlanning.id,
        eventLogTrackerData,
    })
}

interface PostRegisterShiftAction {
    shiftId: string;
    comment?: string;
    history?: { push: (path: string) => void };
}

/**
 * Saga to register a shift and optionally post a comment.
 */
export function* postRegisterShift(action: PostRegisterShiftAction): SagaIterator {
    yield put({ type: SET_LOADING, loading: true })

    const responseTracker = yield call(startEventLoggerApiCall,
        NotificationCenterCategory.SHIFT_PLANNING,
        NotificationCenterAction.CREATED,
        [NotificationCenterActionCategory.SHIFT_PLANNING_REGISTERED],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        yield put({ type: SHIFT_FAILURE, errors: [responseTracker.error] })
        return
    }

    try {
        const response: AxiosResponse = yield call(() => registerShift(action.shiftId, responseTracker.data as NotificationCenterLog))

        if (action.comment) {
            yield call(postCommentToShiftPlanning, action.shiftId, action.comment, responseTracker.data as NotificationCenterLog)
        }

        yield put({
            type: SHIFT_SUCCESS,
            shift: response.data,
        })

        if (action.history) {
            const openShiftsLink = `/${translate('nav.shifts.link')}/${translate('nav.shifts.openShifts.link')}`
            action.history.push(openShiftsLink)
        }
    } catch (error) {
        yield put({ type: SHIFT_FAILURE, errors: [error] })
    } finally {
        yield put({ type: SET_LOADING, loading: false })
        yield call(endEventLoggerApiCall, responseTracker.data as NotificationCenterLog)
    }
}

interface DeleteRegisterShiftAction {
    shiftId: string;
    history?: { push: (path: string) => void };
}

export function* deleteRegisteredShift(action: DeleteRegisterShiftAction): SagaIterator {
    yield put({ type: SET_LOADING, loading: true })

    const responseTracker = yield call(startEventLoggerApiCall,
        NotificationCenterCategory.SHIFT_PLANNING_REMOVAL,
        NotificationCenterAction.DELETED,
        [NotificationCenterActionCategory.SHIFT_PLANNING_REGISTRATION_CANCELLED],
    )

    if (! isFetchResultSuccessful(responseTracker)) {
        yield put({ type: SHIFT_FAILURE, errors: [responseTracker.error] })
        return
    }

    try {
        yield call(() => unregisterShift(action.shiftId, responseTracker.data as NotificationCenterLog))

        if (action.history) {
            const openShiftsLink = `/${translate('nav.shifts.link')}/${translate('nav.shifts.openShifts.link')}`
            action.history.push(openShiftsLink)
        }
    } catch (error) {
        yield put({ type: SHIFT_FAILURE, errors: [error] })
    } finally {
        yield put({ type: SET_LOADING, loading: false })
        yield call(endEventLoggerApiCall, responseTracker.data as NotificationCenterLog)
    }
}

